<template>
    <div class="page-container">
        <div class="content-wrapper">
            <!-- Page Header -->
            <div class="page-header" v-if="isThresholdSettingEnabled">
                <div class="header-left">
                    <h1 class="page-title">Threshold Settings</h1>
                    <p class="page-subtitle">Configure member tier warning notification settings.</p>
                </div>
                <div class="header-actions">
                    <button type="button" class="btn-cancel" @click="$router.push({ name: 'MemberTier' })">Cancel</button>
                    <button type="button" class="btn-save" @click="save" :disabled="loading">
                        <span v-if="loading"><i class="fas fa-spinner fa-spin me-2"></i> Saving...</span>
                        <span v-else><i class="fas fa-check-circle me-2"></i> Save Settings</span>
                    </button>
                </div>
            </div>

            <div v-if="error" class="alert alert-danger mb-4 shadow-sm" role="alert">
                <i class="fas fa-exclamation-triangle me-2"></i> {{ error.message || error }}
            </div>

            <FeatureGate featureKey="threshold_setting">
                <!-- Main Content Grid -->
                <div class="main-grid">
                <!-- Left Column: Main Form -->
                <div class="grid-column main-col content-card">
                    <!-- Section: Configuration -->
                    <div class="card-section">
                        <h3 class="section-title">Notification Configuration</h3>
                        <p class="section-desc">Manage when threshold warnings are triggered.</p>

                        <div class="form-grid mt-4">
                            <div class="form-item form-row">
                                <!-- Percentage -->
                                <div class="form-sub-item">
                                    <label>Warning Percentage <span class="required">*</span></label>
                                    <div class="d-flex align-items-center gap-2">
                                        <div style="flex: 1;">
                                            <o-input-text 
                                                name="percentage" 
                                                v-model="local.percentage" 
                                                type="number" 
                                                min="0" 
                                                max="100"
                                                :autocomplete="'off'"
                                                :required="true"
                                            />
                                        </div>
                                        <span class="fw-bold" style="font-size: 16px; color: #475569;">%</span>
                                    </div>
                                    <small class="form-text text-muted mt-2 d-block">Percentage above minimum tier requirement to trigger warning.</small>
                                    <div v-if="validation.percentage" class="error-text mt-1">{{ validation.percentage[0] }}</div>
                                </div>
                                
                                <!-- Status -->
                                <div class="form-sub-item">
                                    <label>Notification Status</label>
                                    <label class="lock-tier-card" :class="{ active: local.is_active }">
                                        <div class="lock-tier-info">
                                            <i :class="local.is_active ? 'fas fa-bell primary-icon' : 'fas fa-bell-slash text-muted'" class="lock-icon" style="font-size: 20px;"></i>
                                            <div>
                                                <div class="lock-tier-title">Enable Notifications</div>
                                                <div class="lock-tier-desc" style="white-space: nowrap;">Turn on/off warning system.</div>
                                            </div>
                                        </div>
                                        <div class="lock-tier-toggle">
                                            <input type="checkbox" v-model="local.is_active" class="toggle-input" />
                                            <div class="toggle-slider"></div>
                                        </div>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Section: How It Works -->
                    <div class="card-section mt-5 pt-4" style="border-top: 1px solid #f1f5f9;">
                        <h3 class="section-title primary-text"><i class="fas fa-info-circle me-2"></i>How It Works</h3>
                        
                        <div class="row mt-4">
                            <div class="col-md-4">
                                <div class="mb-3">
                                    <strong style="font-size: 13px; color: #1e293b;">Warning Zone:</strong><br>
                                    <span class="text-muted" style="font-size: 13px;">Triggered when a member's transaction total falls within the danger threshold range.</span>
                                </div>
                            </div>
                            <div class="col-md-4">
                                <div class="mb-3">
                                    <strong style="font-size: 13px; color: #1e293b;">Example Scenario:</strong><br>
                                    <span class="text-muted" style="font-size: 13px; line-height: 1.6;">
                                      • Tier minimum: <strong>Rp 25,000,000</strong><br>
                                      • Warning percentage: <strong>12%</strong><br>
                                      • Warning triggers at: <strong class="text-danger">Rp 28,000,000</strong>
                                    </span>
                                </div>
                            </div>
                            <div class="col-md-4">
                                <div class="mb-3">
                                    <strong style="font-size: 13px; color: #1e293b;">Email Frequency:</strong><br>
                                    <span class="text-muted" style="font-size: 13px;">One notification per member per tier period until they move above threshold or get downgraded.</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Right Column: Sidebar -->
                <div class="grid-column side-col">
                    <div class="content-card" style="padding: 24px;" v-if="config">
                        <h3 class="section-title">Current Configuration</h3>
                        
                        <div class="mt-3 p-3" style="background-color: #f8fafc; border-radius: 8px; border: 1px dashed #cbd5e1;">
                            <div class="d-flex justify-content-between mb-2 pb-2" style="border-bottom: 1px solid #e2e8f0;">
                                <span class="text-muted" style="font-size: 13px; font-weight: 500;">Status</span>
                                <span :class="config.is_active ? 'text-success fw-bold' : 'text-warning fw-bold'" style="font-size: 13px;">
                                    <i :class="config.is_active ? 'fas fa-check-circle' : 'fas fa-exclamation-triangle'" class="me-1"></i>
                                    {{ config.is_active ? 'Active' : 'Inactive' }}
                                </span>
                            </div>
                            <div class="d-flex justify-content-between pt-1">
                                <span class="text-muted" style="font-size: 13px; font-weight: 500;">Warning Percentage</span>
                                <span class="primary-text fw-bold" style="font-size: 14px;">{{ config.percentage }}%</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            </FeatureGate>
        </div>

        <!-- Global Loading Overlay -->
        <div v-if="loading" class="global-loader">
            <div class="spinner-border primary-text" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { OInputText } from '@/components/OmnihControl'
import FeatureGate from '@/components/upsell/FeatureGate.vue'
import { useToast } from '@/composables/useToast'

export default {
    name: 'ThresholdSettings',
    components: {
        OInputText,
        FeatureGate
    },
    setup() {
        const { showToast } = useToast()
        return { showToast }
    },
    data() {
        return { 
            local: { percentage: 20, is_active: true }, 
            validation: {}
        }
    },
    computed: {
        ...mapGetters('threshold', ['config', 'loading', 'error']),
        isThresholdSettingEnabled() {
            return (this.$store.state.FeatureFlag?.thresholdSetting?.flag || 'ACTIVE') === 'ACTIVE'
        }
    },
    watch: {
        config: {
            immediate: true,
            deep: true,
            handler(val) { 
                if (val && Object.keys(val).length > 0) {
                    this.local = { 
                        percentage: val.percentage, 
                        is_active: !!val.is_active 
                    }
                }
            }
        }
    },
    created() { 
        this.fetch() 
    },
    methods: {
        ...mapActions('threshold', ['fetch', 'update']),
        async save() {
            this.validation = {}
            
            // Basic validation
            if (this.local.percentage === null || this.local.percentage === '' || this.local.percentage < 0 || this.local.percentage > 100) {
                this.validation.percentage = ['Percentage must be between 0 and 100']
                return
            }
            
            try {
                const res = await this.update(this.local)
                const msg = res?.message || 'Threshold updated successfully'
                this.showToast({ type: 'success', title: 'Success', message: msg })
                this.$router.push({ name: 'MemberTier' })
            } catch (error) {
                console.error('Error updating threshold:', error)
                this.showToast({ 
                    type: 'error', 
                    title: 'Error', 
                    message: error.response?.data?.message || error.message || 'Failed to update threshold' 
                })
            }
        }
    }
}
</script>

<style scoped>
/* Feature Disabled State */
.feature-disabled-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    padding: 2rem;
}

.disabled-content {
    text-align: center;
    color: #6b7280;
}

.disabled-content h4 {
    color: #374151;
    margin-bottom: 0.5rem;
    font-weight: 600;
}

.disabled-content p {
    margin: 0;
    font-size: 0.95rem;
}

/* Page Layout */
.page-container {
    width: 100%;
    min-height: calc(100vh - 64px);
    background-color: #f1f4f9;
    padding: 24px 40px;
}

.content-wrapper {
    max-width: 1240px;
    margin: 0 auto;
}

/* Page Header */
.page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 32px;
    flex-wrap: wrap;
    gap: 16px;
}

.page-title {
    font-size: 26px;
    font-weight: 800;
    color: #0f172a;
    letter-spacing: -0.5px;
    margin: 0;
}

.page-subtitle {
    color: #64748b;
    font-size: 14px;
    margin: 4px 0 0 0;
}

.header-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
}

/* Buttons */
.btn-cancel {
    padding: 10px 24px;
    border: 1px solid #e2e8f0;
    background: white;
    color: #475569;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s, border-color 0.2s;
}

.btn-cancel:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
}

.btn-save {
    padding: 10px 24px;
    background: var(--primary-color, #fc6666);
    border: none;
    color: white;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    box-shadow: 0 4px 6px -1px var(--primary-subtle, rgba(252, 102, 102, 0.2));
}

.btn-save:hover {
    filter: brightness(0.9);
    box-shadow: 0 10px 15px -3px var(--primary-subtle, rgba(252, 102, 102, 0.3));
}

.btn-save:disabled {
    background: #cbd5e1;
    box-shadow: none;
    cursor: not-allowed;
}

/* Grid Layout */
.main-grid {
    display: grid;
    grid-template-columns: 1fr 340px;
    gap: 24px;
    align-items: start;
}

/* Content Cards */
.content-card {
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    padding: 28px 32px;
    border: 1px solid rgba(0, 0, 0, 0.05);
}

.section-title {
    font-size: 17px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 4px;
}

.section-desc {
    font-size: 13px;
    color: #64748b;
    margin-bottom: 20px;
}

/* Form Styling */
.form-grid {
    display: flex;
    flex-direction: column;
    gap: 18px;
}

.form-item label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: #475569;
    margin-bottom: 6px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.form-item.form-row {
    display: flex;
    gap: 24px;
}

.form-sub-item {
    flex: 1;
    min-width: 0;
}

.required {
    color: #ef4444;
}

.error-text {
    color: #ef4444;
    font-size: 12px;
    margin-top: 5px;
    font-weight: 500;
}

.text-muted {
    color: #64748b !important;
}

.text-danger {
    color: #ef4444 !important;
}

.text-success {
    color: #22c55e !important;
}

/* Input Fields Overrides */
:deep(input) {
    width: 100%;
    padding: 11px 14px !important;
    border: 1.5px solid #e2e8f0 !important;
    border-radius: 8px !important;
    font-size: 14px !important;
    color: #0f172a !important;
    background-color: #f8fafc !important;
    transition: all 0.2s;
}

:deep(input:focus) {
    border-color: var(--primary-color, #fc6666) !important;
    background-color: #ffffff !important;
    box-shadow: 0 0 0 3px var(--primary-subtle, rgba(252, 102, 102, 0.1)) !important;
    outline: none !important;
}

:deep(.input-wrapper) {
    margin: 0 !important;
}

/* Lock Tier Card (For Enable Notifications) */
.lock-tier-card {
    display: flex !important;
    align-items: center;
    justify-content: space-between;
    padding: 14px 18px;
    border: 1.5px solid #e2e8f0;
    border-radius: 8px;
    background: #f8fafc;
    cursor: pointer;
    transition: all 0.2s;
    user-select: none;
    margin-bottom: 0 !important;
    min-width: 280px;
}

.lock-tier-card:hover {
    border-color: #cbd5e1;
}

.lock-tier-card.active {
    border-color: var(--primary-color, #fc6666);
    background: var(--secondary-color, #fffafa);
}

.lock-tier-info {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
}

.lock-icon {
    font-size: 18px;
    transition: color 0.2s;
}

.lock-tier-title {
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
    margin-bottom: 2px;
    text-transform: none;
    letter-spacing: normal;
}

.lock-tier-desc {
    font-size: 12px;
    color: #64748b;
    text-transform: none;
    font-weight: 400;
    letter-spacing: normal;
}

/* Custom Toggle */
.lock-tier-toggle {
    position: relative;
    width: 44px;
    height: 24px;
    flex-shrink: 0;
}

.toggle-input {
    opacity: 0;
    width: 0;
    height: 0;
}

.toggle-slider {
    position: absolute;
    inset: 0;
    background: #cbd5e1;
    border-radius: 99px;
    transition: background 0.2s;
    cursor: pointer;
}

.toggle-slider::before {
    content: '';
    position: absolute;
    width: 18px;
    height: 18px;
    left: 3px;
    top: 3px;
    background: white;
    border-radius: 50%;
    transition: transform 0.2s;
    box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}

.toggle-input:checked + .toggle-slider {
    background: var(--primary-color, #fc6666);
}

.toggle-input:checked + .toggle-slider::before {
    transform: translateX(20px);
}

/* Global Loader */
.global-loader {
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(2px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
}

/* Responsive */
@media (max-width: 1200px) {
    .main-grid {
        grid-template-columns: 1fr;
    }
    
    .page-container {
        padding: 24px 20px;
    }
}

@media (max-width: 991px) {
    .form-item.form-row {
        flex-direction: column;
        gap: 24px;
    }
    
    .lock-tier-card {
        min-width: 100%;
        width: 100%;
    }
}

@media (max-width: 640px) {
    .page-container {
        padding: 16px 16px;
    }
    .page-header {
        flex-direction: column;
        align-items: flex-start;
    }
    .header-actions {
        width: 100%;
        margin-top: 8px;
    }
    .btn-save, .btn-cancel {
        flex: 1;
        justify-content: center;
    }
    .content-card {
        padding: 20px 16px;
    }
}
</style>
